import React from 'react';
import { Box, SimpleGrid, Skeleton, Text, VStack } from '@chakra-ui/react';
import { CompanyStats } from '../../../store/responseTypes/company';

const TABULAR = { fontVariantNumeric: 'tabular-nums' as const };

const formatNumber = (n: number) => n.toLocaleString('en-US');

const formatBytes = (bytes: number): string => {
  if (!bytes) return '0';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log10(bytes) / 3));
  const v = bytes / Math.pow(1000, i);
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
};

interface CompanyKpiGridProps {
  stats?: CompanyStats;
  isLoading?: boolean;
}

interface KpiTileProps {
  label: string;
  value: string;
  sub?: string;
}

const KpiTile: React.FC<KpiTileProps> = ({ label, value, sub }) => (
  <VStack align="start" spacing={2}>
    <Text
      fontSize="xs"
      letterSpacing="0.18em"
      textTransform="uppercase"
      color="gray.400"
      fontWeight={500}
    >
      {label}
    </Text>
    <Text
      fontSize={{ base: '3xl', md: '4xl' }}
      fontWeight={500}
      color="gray.900"
      letterSpacing="-0.02em"
      lineHeight={1}
      sx={TABULAR}
    >
      {value}
    </Text>
    {sub && (
      <Text fontSize="xs" color="gray.500" sx={TABULAR}>
        {sub}
      </Text>
    )}
  </VStack>
);

interface StorageTileProps {
  usedBytes: number;
  capacityMegabytes: number;
}

const StorageTile: React.FC<StorageTileProps> = ({
  usedBytes,
  capacityMegabytes,
}) => {
  const capacityBytes = capacityMegabytes * 1000 * 1000;
  const pct =
    capacityBytes > 0 ? Math.min(100, (usedBytes / capacityBytes) * 100) : 0;
  return (
    <VStack align="start" spacing={2}>
      <Text
        fontSize="xs"
        letterSpacing="0.18em"
        textTransform="uppercase"
        color="gray.400"
        fontWeight={500}
      >
        Storage
      </Text>
      <Text
        fontSize={{ base: '3xl', md: '4xl' }}
        fontWeight={500}
        color="gray.900"
        letterSpacing="-0.02em"
        lineHeight={1}
        sx={TABULAR}
      >
        {formatBytes(usedBytes)}
      </Text>
      <Text fontSize="xs" color="gray.500" sx={TABULAR}>
        of {formatBytes(capacityBytes)}
      </Text>
      <Box
        w="100%"
        h="1px"
        bg="gray.200"
        position="relative"
        mt={1}
      >
        <Box
          position="absolute"
          left={0}
          top={0}
          h="1px"
          bg="gray.900"
          w={`${pct}%`}
        />
      </Box>
    </VStack>
  );
};

const LoadingTile: React.FC = () => (
  <VStack align="start" spacing={2}>
    <Skeleton height="10px" width="80px" />
    <Skeleton height="32px" width="120px" />
    <Skeleton height="10px" width="100px" />
  </VStack>
);

const CompanyKpiGrid: React.FC<CompanyKpiGridProps> = ({ stats, isLoading }) => {
  if (isLoading || !stats) {
    return (
      <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={{ base: 6, md: 10 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <LoadingTile key={i} />
        ))}
      </SimpleGrid>
    );
  }

  return (
    <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={{ base: 6, md: 10 }}>
      <KpiTile label="Signed agreements" value={formatNumber(stats.signedAgreements)} />
      <KpiTile label="Registered agreements" value={formatNumber(stats.registeredAgreements)} />
      <KpiTile label="Projects" value={formatNumber(stats.projects)} />
      <KpiTile
        label="User sessions"
        value={formatNumber(stats.userSessionsInPeriod)}
        sub="in period"
      />
      <KpiTile label="Files uploaded" value={formatNumber(stats.filesUploaded)} />
      <StorageTile
        usedBytes={stats.storageUsedBytes}
        capacityMegabytes={stats.storageCapacityMegabytes}
      />
      <KpiTile label="Admin users" value={formatNumber(stats.adminUsers)} />
      <KpiTile label="Employee users" value={formatNumber(stats.employeeUsers)} />
    </SimpleGrid>
  );
};

export default CompanyKpiGrid;
